import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchBar from '../components/SearchBar';
import apiService from '../services/api';

function Home() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await apiService.getStats();
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const handleSearch = (query) => {
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  return (
    <div className="container">
      {/* Hero Section */}
      <div style={{ textAlign: 'center', padding: '3rem 0' }}>
        <h1 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
          Sidekick Drug Knowledge Graph
        </h1>
        <p style={{ fontSize: '1.25rem', color: 'var(--text-secondary)', marginBottom: '2rem' }}>
          Explore drug indications, contraindications, and side effects from FDA labels
        </p>

        {/* Search Bar */}
        <div style={{ maxWidth: '600px', margin: '0 auto 3rem' }}>
          <SearchBar onSearch={handleSearch} />
        </div>

        {/* Stats */}
        {loading ? (
          <div className="loading">Loading statistics...</div>
        ) : stats && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {stats.SIO_010039 && (
              <div className="card">
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary)' }}>
                  {stats.SIO_010039.toLocaleString()}
                </div>
                <div style={{ color: 'var(--text-secondary)' }}>Drug Products</div>
              </div>
            )}
            {stats.DrugCollection && (
              <div className="card">
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary)' }}>
                  {stats.DrugCollection.toLocaleString()}
                </div>
                <div style={{ color: 'var(--text-secondary)' }}>Ingredient Combinations</div>
              </div>
            )}
            {stats.SideEffect && (
              <div className="card">
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--danger)' }}>
                  {stats.SideEffect.toLocaleString()}
                </div>
                <div style={{ color: 'var(--text-secondary)' }}>Side Effects</div>
              </div>
            )}
            {stats.SIO_000148 && (
              <div className="card">
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary)' }}>
                  {stats.SIO_000148.toLocaleString()}
                </div>
                <div style={{ color: 'var(--text-secondary)' }}>FDA SPL Documents</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* About Section */}
      <div className="card" style={{ maxWidth: '800px', margin: '2rem auto' }}>
        <h2 style={{ marginBottom: '1rem' }}>About Sidekick</h2>
        <p style={{ marginBottom: '1rem', lineHeight: '1.6' }}>
          Sidekick is a comprehensive knowledge graph of drug information extracted from FDA 
          Structured Product Labels (SPLs). Our data includes:
        </p>
        <ul style={{ marginLeft: '1.5rem', lineHeight: '1.8' }}>
          <li>Drug indications (what conditions drugs treat)</li>
          <li>Drug contraindications (when drugs should not be used)</li>
          <li>Side effects and adverse reactions</li>
          <li>Drug-drug interactions</li>
        </ul>
        <p style={{ marginTop: '1rem', lineHeight: '1.6' }}>
          All information is mapped to standard ontologies including HPO (Human Phenotype Ontology),
          MONDO (Disease Ontology), and RxNorm for interoperability.
        </p>
      </div>

      {/* Features */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1rem',
        marginTop: '2rem'
      }}>
        <div className="card">
          <h3 style={{ marginBottom: '0.5rem' }}>🔍 Search</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            Search by ingredient name, RxCUI, or product name with intelligent autocomplete
          </p>
        </div>
        <div className="card">
          <h3 style={{ marginBottom: '0.5rem' }}>🔬 SPARQL Query</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            Run custom SPARQL queries with pre-loaded examples for power users
          </p>
        </div>
        <div className="card">
          <h3 style={{ marginBottom: '0.5rem' }}>📊 Explore</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            Discover statistics and trends across the entire drug knowledge base
          </p>
        </div>
      </div>
    </div>
  );
}

export default Home;
